import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ChevronDown } from "lucide-react";

const primaryNav = [
  { to: "/vigil", label: "The Vigil" },
  { to: "/answered", label: "Answered" },
];

const exploreNav = [
  { to: "/still-being-held", label: "Still Being Held" },
  { to: "/words", label: "Words for the Way" },
  { to: "/practices", label: "Practices" },
  { to: "/our-practice", label: "Our Practice" },
  { to: "/about", label: "About" },
];

const publicNav = [...primaryNav, ...exploreNav];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const exploreRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (exploreRef.current && !exploreRef.current.contains(e.target)) setExploreOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="App min-h-screen flex flex-col bg-paper">
      <header className="border-b border-ink/15 bg-paper sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="leading-none">
            <div className="font-heading text-2xl md:text-3xl text-ink tracking-tight">Ministry of Answered Prayers</div>
            <div className="mono-label mt-1 hidden sm:block">Ask · Attend · Wait · Notice · Give thanks</div>
          </Link>
          <nav className="hidden lg:flex items-center gap-7">
            {primaryNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `mono-label hover:text-ink ${isActive ? "text-ink" : ""}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="relative" ref={exploreRef}>
              <button
                onClick={() => setExploreOpen((v) => !v)}
                data-testid="nav-explore"
                className="mono-label hover:text-ink flex items-center gap-1"
                aria-expanded={exploreOpen}
              >
                Explore <ChevronDown size={13} className={`transition-transform ${exploreOpen ? "rotate-180" : ""}`} />
              </button>
              {exploreOpen && (
                <div className="absolute left-0 top-full mt-3 w-52 bg-paper border border-ink/20 py-2 z-50" data-testid="explore-menu">
                  {exploreNav.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      onClick={() => setExploreOpen(false)}
                      data-testid={`nav-${n.to.replace("/", "")}`}
                      className={({ isActive }) => `block px-4 py-2 mono-label hover:bg-paper-tertiary hover:text-ink ${isActive ? "text-ink" : ""}`}
                    >
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>
          <div className="hidden lg:flex items-center gap-5">
            {user && user.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin" className="mono-label hover:text-ink">Admin</Link>
            )}
            {user ? (
              <>
                <Link to="/my-list" data-testid="nav-mylist" className="mono-label hover:text-ink">My List</Link>
                <button onClick={onLogout} data-testid="nav-logout" className="mono-label hover:text-ink">Sign out</button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login" className="mono-label hover:text-ink">Sign in</Link>
            )}
            <Link to="/submit" data-testid="nav-submit" className="btn-solid">Submit a Prayer</Link>
          </div>
          <button className="lg:hidden text-ink" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-ink/15 px-6 py-5 flex flex-col gap-4" data-testid="mobile-menu">
            {publicNav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="mono-label">{n.label}</Link>
            ))}
            <div className="paper-rule pt-4 flex flex-col gap-4">
              <Link to="/submit" onClick={() => setOpen(false)} className="mono-label text-ink">Submit a Prayer</Link>
              {user && user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="mono-label">Admin</Link>}
              {user ? (
                <>
                  <Link to="/my-list" onClick={() => setOpen(false)} className="mono-label">My List</Link>
                  <Link to="/morning" onClick={() => setOpen(false)} className="mono-label">Morning Prayer</Link>
                  <Link to="/evening" onClick={() => setOpen(false)} className="mono-label">Evening Prayer</Link>
                  <button onClick={() => { setOpen(false); onLogout(); }} className="mono-label text-left">Sign out</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="mono-label">Sign in</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-ink/15 mt-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 grid gap-10 md:grid-cols-3">
          <div>
            <div className="font-heading text-xl text-ink">Ministry of Answered Prayers</div>
            <p className="font-body text-ink-secondary mt-3 max-w-xs leading-relaxed italic">
              May my life today become an answer to someone's prayer.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <span className="mono-label mb-1">The Circle</span>
            <Link to="/vigil" className="font-body text-ink-secondary hover:text-ink">The Vigil</Link>
            <Link to="/answered" className="font-body text-ink-secondary hover:text-ink">Answered Prayers</Link>
            <Link to="/still-being-held" className="font-body text-ink-secondary hover:text-ink">Still Being Held</Link>
            <Link to="/words" className="font-body text-ink-secondary hover:text-ink">Words for the Way</Link>
          </div>
          <div className="flex flex-col gap-2">
            <span className="mono-label mb-1">The Ministry</span>
            <Link to="/about" className="font-body text-ink-secondary hover:text-ink">About</Link>
            <Link to="/our-practice" className="font-body text-ink-secondary hover:text-ink">Our Practice</Link>
            <Link to="/practices" className="font-body text-ink-secondary hover:text-ink">Practices</Link>
            <Link to="/policies" className="font-body text-ink-secondary hover:text-ink">Terms & Privacy</Link>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 md:px-10 pb-10">
          <div className="paper-rule pt-6 mono-label">
            Est. 2026 · A quiet prayer community · Not an emergency or crisis service
          </div>
        </div>
      </footer>
    </div>
  );
}
